import React, { useCallback, useContext } from 'react'

import ItemPriceAndCTAs from './common/ItemPriceAndCTAs'

import { fareTypeLabelMap, FlightsFareTypes } from 'constants/flight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api'
import { FlightItem } from 'tripPlanner/types/tripItem'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'
import TextButton from 'components/Luxkit/Button/TextButton'

interface Props {
  item: FlightItem
  buttonKind: React.ComponentProps<typeof TextButton>['kind']
}

function FlightSearchPriceAndCTAs({ item, buttonKind }: Props) {
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareType,
      totalPrice,
      travellerCounts,
    } = {},
  } = item

  const effectiveCurrencyCode = viewAsTemplate ? currentCurrency : (currencyCode ?? currentCurrency)
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode: viewAsTemplate ? currentRegionCode : (regionCode ?? currentRegionCode),
    shouldQuery:
      (viewAsTemplate || !adultPrice) && fareType === FlightsFareTypes.RETURN,
  })

  // when viewed in a curated trip, ignore saved flight price
  const minAdultPrice = viewAsTemplate ? price : adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const fareTypeLabel = fareType ? fareTypeLabelMap[fareType] : undefined

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }
  const showTotal = totalPrice && sumUpOccupancies([travellerCount]) > 1

  const { setImmersiveMode } = useImmersiveModeHandlers()
  const onFlightSearchClick = useCallback(() => {
    setImmersiveMode({
      sectionId: itemSectionId(item.id),
      tripId: item.tripId,
    })
  }, [item.id, item.tripId, setImmersiveMode])

  const source = useTripClientSource()
  const flightSearchUrl = item.savedItemData ? addQueryParamsToPath(
    buildStandaloneFlightSearchURL(item.savedItemData),
    {
      source,
    },
  ) : undefined

  return (
    <ItemPriceAndCTAs>
      {showPrice && <div className="price-area">
        <Caption variant="medium">{fareTypeLabel} from</Caption>
        <div>
          <Heading as="span" variant="heading6">
            <FormatCurrency
              value={minAdultPrice}
              currency={effectiveCurrencyCode}
              format="dynamicDollar"
            />
            *
          </Heading>
          <BodyText as="span" variant="small">
            {' '}
            /adult
          </BodyText>
          {showTotal && (
            <Caption variant="medium" colour="neutral-three">
              Total from <FormatCurrency value={totalPrice} />
            </Caption>
          )}
          <Caption colour="neutral-four" variant="medium">
            *Price and availability are subject to change.
          </Caption>
        </div>
      </div>}
      {flightSearchUrl && <div className="cta-area">
        <TextButton
          kind={buttonKind}
          to={flightSearchUrl}
          onClick={onFlightSearchClick}
        >
          Search flights
        </TextButton>
      </div>}
    </ItemPriceAndCTAs>
  )
}

export default FlightSearchPriceAndCTAs
